import { truncateString, useIntl } from '@capsicum/common';
import styled, { css, useTheme } from 'styled-components';

import { env } from '../../../util/env';
import { InfoTooltip, Text } from '../../atoms';
import { ExternalLinkIcon } from '../../icons/ExternalLink';
import { CopyToClipboard } from '../../molecules/copy-to-clipboard/CopyToClipboard';

export type SmartContractAddressProps = {
	contractAddress: string;
	path: 'address' | 'token';
	testID: string;
	variant: 'copy' | 'link';
};

const wrapperStyle = css`
	width: auto;
`;

export const SmartContractAddress = ({
	contractAddress,
	path = 'token',
	testID,
	variant = 'link'
}: SmartContractAddressProps) => {
	const { $t } = useIntl();
	const { colors } = useTheme();

	return (
		<S.Container>
			<S.LabelAndTooltipWrapper>
				<Text variant="text3">{$t('payments.label.smartContract')}</Text>
				<InfoTooltip testID={`${testID}-tooltip`} zIndex={12}>
					<S.TooltipText variant="text3">{$t('payments.alerts.smartContractAddressTooltip')}</S.TooltipText>
				</InfoTooltip>
			</S.LabelAndTooltipWrapper>

			{variant === 'link' ? (
				<S.LinkContainer>
					<S.LinkText
						href={`${env.NEXT_PUBLIC_CHILISCAN_URL}/${path}/${contractAddress}`}
						rel="noreferrer"
						target="_blank"
					>
						{truncateString(contractAddress, 8, 8)}
						<ExternalLinkIcon color={colors.text.brand.enabled} />
					</S.LinkText>
				</S.LinkContainer>
			) : (
				<CopyToClipboard
					label={$t('payments.alerts.contractAddress')}
					testID={`${testID}-copyToClipboard`}
					value={contractAddress}
					wrapperStyle={wrapperStyle}
				/>
			)}
		</S.Container>
	);
};

namespace S {
	export const Container = styled.div`
		width: 100%;
		box-sizing: border-box;
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		border-radius: ${({ theme }) => theme.sizing.xxsmall};
		padding: ${({ theme }) => theme.sizing.xsmall};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;

	export const LabelAndTooltipWrapper = styled.div`
		display: flex;
		flex-direction: row;
		gap: ${({ theme }) => theme.spacings.xxxsmall};
		align-items: center;
	`;
	export const TooltipText = styled(Text)`
		${({ theme }) => theme.fonts.text.text3};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const LinkText = styled.a`
		${({ theme }) => theme.fonts.text.text1};
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		text-decoration: none;
	`;

	export const LinkContainer = styled.div`
		flex-direction: row;
		align-items: center;
	`;
}
